import React, { useEffect, useState } from 'react';
import {
  Navigation,
  MapPin,
  Clock,
  CheckCircle2,
  ExternalLink,
  ChevronRight,
  Sparkles,
  AlertCircle,
  Truck,
  Layers,
  ArrowRight,
  ShieldCheck,
  CalendarDays
} from 'lucide-react';
import { useAreaMotorista } from '../../contexts/AreaMotoristaContext';
import { roteiroService } from '../../services/roteiroService';
import { RoteiroMotorista, RoteiroItem } from '../../types/roteiro';
import { useNavigate } from 'react-router-dom';

function isDataHoje(dateStr?: string): boolean {
  if (!dateStr) return false;
  const clean = dateStr.split('T')[0].trim();
  const hojeIso = new Date().toISOString().split('T')[0];
  const hojeLocal = new Date().toLocaleDateString('sv-SE');
  return clean === hojeIso || clean === hojeLocal;
}

export const AreaMotoristaRoteiroDia: React.FC = () => {
  const { currentMotorista, ordensServico, setViagemAtiva } = useAreaMotorista();
  const navigate = useNavigate();

  const [roteirosAdmin, setRoteirosAdmin] = useState<RoteiroMotorista[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [paradasConcluidas, setParadasConcluidas] = useState<Record<number, boolean>>({});

  useEffect(() => {
    async function loadRoteiro() {
      if (!currentMotorista?.id) {
        setCarregando(false);
        return;
      }
      setCarregando(true);
      try {
        const data = await roteiroService.getRoteirosMotorista(currentMotorista.id);
        
        // Filtra apenas rotas definidas pelo painel administrativo para a data de hoje ou em execução
        const definidasPeloAdmin = (data || []).filter(r => {
          if (r.status === 'cancelado') return false;
          return isDataHoje(r.data_roteiro) || r.status === 'em_execucao';
        });

        setRoteirosAdmin(definidasPeloAdmin);
      } catch (err) {
        console.error('Erro ao buscar roteiros do motorista:', err);
      } finally {
        setCarregando(false);
      }
    }

    loadRoteiro();
  }, [currentMotorista]);

  // Regra de Negócio:
  // 1. As rotas que aparecem para o motorista devem ser APENAS as que são definidas pelo painel administrativo.
  // 2. Caso não haja rota definida pelo painel admin, devem aparecer APENAS as Ordens de Serviço do dia (data_atendimento = hoje).
  const temRotaAdmin = roteirosAdmin.length > 0;
  const roteiroAtivo = temRotaAdmin ? roteirosAdmin[0] : null;

  // Ordens de Serviço exclusivamente do dia atual
  const ordensServicoHoje = ordensServico.filter(os => {
    if (os.status_id === 3) return false; // Exclui canceladas
    return isDataHoje(os.data_atendimento) || isDataHoje(os.created_at);
  });

  const paradas: RoteiroItem[] = temRotaAdmin && roteiroAtivo?.itens && roteiroAtivo.itens.length > 0
    ? roteiroAtivo.itens
    : roteiroService.converterOSToParadas(ordensServicoHoje);

  const handleOpenGoogleMaps = (endereco?: string) => {
    if (!endereco) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(endereco)}`, '_blank');
  };

  const handleOpenWaze = (endereco?: string) => {
    if (!endereco) return;
    window.open(`https://waze.com/ul?q=${encodeURIComponent(endereco)}&navigate=yes`, '_blank');
  };

  const handleIniciarViagem = async () => {
    if (roteiroAtivo) {
      await roteiroService.atualizarStatusRoteiro(roteiroAtivo.id, 'em_execucao');
      setRoteirosAdmin(prev => prev.map(r => r.id === roteiroAtivo.id ? { ...r, status: 'em_execucao' } : r));
    }
  };

  const handleAlternarParadaConcluida = async (seq: number) => {
    const novoStatus = !paradasConcluidas[seq];
    setParadasConcluidas(prev => ({ ...prev, [seq]: novoStatus }));

    if (roteiroAtivo) {
      if (roteiroAtivo.status !== 'em_execucao') {
        await roteiroService.atualizarStatusRoteiro(roteiroAtivo.id, 'em_execucao');
        setRoteirosAdmin(prev => prev.map(r => r.id === roteiroAtivo.id ? { ...r, status: 'em_execucao' } : r));
      }
      await roteiroService.atualizarStatusParada(
        roteiroAtivo.id,
        seq,
        novoStatus ? 'concluida' : 'pendente'
      );
    }
  };

  const handleIrParaExecucaoOS = (osId: number) => {
    const os = ordensServico.find(o => o.id === osId);
    if (os) {
      setViagemAtiva(os);
      navigate('/area-motorista/execucao');
    }
  };

  if (carregando) {
    return <div className="p-12 text-center text-xs text-slate-500">Carregando programação do dia...</div>;
  }

  return (
    <div className="space-y-5 text-left">
      {/* Header com Resumo do Roteiro */}
      <div className="p-5 sm:p-6 rounded-3xl bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-red-600/20 text-red-500 flex items-center justify-center border border-red-500/30 shrink-0">
            <Navigation className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-black text-white">
                {temRotaAdmin
                  ? (roteiroAtivo?.nome_roteiro || 'Roteiro Definido pelo Painel Administrative')
                  : 'Ordens de Serviço do Dia'}
              </h1>
              {temRotaAdmin ? (
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-emerald-950 text-emerald-400 border border-emerald-800 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-emerald-400" />
                  Rota do Painel Admin
                </span>
              ) : (
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-amber-950 text-amber-400 border border-amber-800 flex items-center gap-1">
                  <CalendarDays className="w-3 h-3 text-amber-400" />
                  OSs do Dia
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              {temRotaAdmin
                ? 'Sequência e paradas definidas oficialmente pelo Painel Administrativo.'
                : 'Nenhum roteiro definido pelo painel admin. Exibindo apenas as Ordens de Serviço agendadas para hoje.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {temRotaAdmin && roteiroAtivo && roteiroAtivo.status !== 'em_execucao' && roteiroAtivo.status !== 'concluido' && (
            <button
              onClick={handleIniciarViagem}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-600 to-amber-600 hover:from-red-500 hover:to-amber-500 text-white text-xs font-black flex items-center gap-2 shadow-lg transition animate-bounce"
            >
              <Truck className="w-4 h-4" />
              <span>🚀 INICIAR ROTA ADMIN</span>
            </button>
          )}

          <span className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300">
            {paradas.length} {paradas.length === 1 ? 'Parada Programada' : 'Paradas Programadas'}
          </span>
        </div>
      </div>

      {/* Lista de Etapas / Paradas em Linha do Tempo */}
      {paradas.length === 0 ? (
        <div className="p-12 text-center rounded-3xl border border-slate-800 bg-slate-950/60 space-y-3">
          <Truck className="w-10 h-10 mx-auto text-slate-600 stroke-[1.5]" />
          <h3 className="text-sm font-bold text-white">Nenhuma atividade agendada para hoje</h3>
          <p className="text-xs text-slate-400 max-w-md mx-auto">
            Você não possui rotas definidas pelo painel administrativo nem ordens de serviço agendadas para a data de hoje.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {paradas.map((parada, idx) => {
            const isConcluida = paradasConcluidas[parada.ordem_sequencia || (idx + 1)] || parada.status_parada === 'concluida';
            const isColeta = parada.tipo_parada === 'coleta';

            return (
              <div
                key={idx}
                className={`p-4 sm:p-5 rounded-3xl border transition-all duration-200 relative overflow-hidden ${
                  isConcluida
                    ? 'bg-slate-950/40 border-slate-800 opacity-60'
                    : isColeta
                    ? 'bg-gradient-to-br from-amber-950/30 to-slate-950 border-amber-800/60 shadow-lg'
                    : 'bg-gradient-to-br from-emerald-950/30 to-slate-950 border-emerald-800/60 shadow-lg'
                }`}
              >
                {/* Linha vertical conectando paradas */}
                {idx < paradas.length - 1 && (
                  <div className="absolute left-7 top-16 bottom-0 w-0.5 bg-slate-800 -z-0" />
                )}

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  
                  {/* Etapa com Número */}
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`w-9 h-9 rounded-2xl flex items-center justify-center font-black text-xs shrink-0 shadow-md ${
                        isConcluida
                          ? 'bg-emerald-600 text-white'
                          : isColeta
                          ? 'bg-amber-500 text-slate-950 font-black'
                          : 'bg-emerald-500 text-slate-950 font-black'
                      }`}
                    >
                      {isConcluida ? <CheckCircle2 className="w-5 h-5" /> : `#${idx + 1}`}
                    </div>

                    <div className="space-y-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${
                            isColeta
                              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                              : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          }`}
                        >
                          {isColeta ? '1. Coleta / Retirada' : '2. Entrega / Descarga'}
                        </span>

                        <span className="text-xs font-bold text-white">
                          OS #{parada.ordem_de_servico_id}
                        </span>

                        {parada.prioridade && parada.prioridade !== 'normal' && (
                          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-red-950 text-red-300 border border-red-800">
                            {parada.prioridade.toUpperCase()} 🚨
                          </span>
                        )}
                      </div>

                      <p className="text-xs sm:text-sm font-semibold text-slate-200 leading-snug">
                        📍 {parada.endereco}
                      </p>

                      {parada.descricao_carga && (
                        <p className="text-[11px] text-slate-400">
                          📦 {parada.descricao_carga}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Ações de GPS e Execução */}
                  <div className="flex items-center gap-2 self-end sm:self-center shrink-0 pt-2 sm:pt-0">
                    <button
                      onClick={() => handleOpenGoogleMaps(parada.endereco)}
                      className="px-2.5 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-[11px] font-bold text-slate-300 flex items-center gap-1 transition"
                    >
                      <span>Maps</span>
                      <ExternalLink className="w-3 h-3 text-slate-400" />
                    </button>

                    <button
                      onClick={() => handleOpenWaze(parada.endereco)}
                      className="px-2.5 py-1.5 rounded-xl bg-sky-950/80 hover:bg-sky-900 border border-sky-800 text-[11px] font-bold text-sky-300 flex items-center gap-1 transition"
                    >
                      <span>Waze</span>
                    </button>

                    <button
                      onClick={() => handleIrParaExecucaoOS(parada.ordem_de_servico_id)}
                      className="px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-1 transition shadow-md"
                    >
                      <span>Executar OS</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={() => handleAlternarParadaConcluida(parada.ordem_sequencia || (idx + 1))}
                      className={`p-1.5 rounded-xl border transition ${
                        isConcluida
                          ? 'bg-emerald-950 border-emerald-700 text-emerald-400'
                          : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white'
                      }`}
                      title={isConcluida ? 'Marcar como pendente' : 'Marcar como concluída'}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
